import React, { useMemo, useState } from 'react'
import { useLayoutStore } from '@/stores/layoutStore'
import { useUIStore } from '@/stores/uiStore'
import { Device, DeviceCategory } from '@/types/devices'
import {
  CATEGORY_COLORS,
  CATEGORY_LABELS,
  CategoryIcon,
} from '@/constants/categories'
import { EditDeviceDialog } from './EditDeviceDialog'

interface ManageDevicesPanelProps {
  isOpen: boolean
  onClose: () => void
}

type SortOption = 'name' | 'category' | 'connections'

export const ManageDevicesPanel: React.FC<ManageDevicesPanelProps> = ({
  isOpen,
  onClose,
}) => {
  const devices = useLayoutStore(s => s.devices)
  const connections = useLayoutStore(s => s.connections)
  const deleteDevice = useLayoutStore(s => s.deleteDevice)
  const updateDevice = useLayoutStore(s => s.updateDevice)
  const [editingDevice, setEditingDevice] = useState<Device | null>(null)
  const selectedIds = useUIStore(s => s.selectedIds)
  const setSelection = useUIStore(s => s.setSelection)
  const toggleSelection = useUIStore(s => s.toggleSelection)
  const clearSelection = useUIStore(s => s.clearSelection)

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<
    DeviceCategory | 'all'
  >('all')
  const [sortBy, setSortBy] = useState<SortOption>('name')
  const [localSelected, setLocalSelected] = useState<Set<string>>(new Set())

  // Get all unique categories
  const categories = useMemo(() => {
    const cats = new Set<DeviceCategory>(devices.map(d => d.category))
    return Array.from(cats).sort()
  }, [devices])

  // Calculate connection count for a device
  const getConnectionCount = (deviceId: string): number => {
    return connections.filter(
      c => c.sourceDeviceId === deviceId || c.targetDeviceId === deviceId,
    ).length
  }

  // Filter devices
  const filteredDevices = useMemo(() => {
    return devices.filter(device => {
      const matchesSearch =
        device.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (device.manufacturer
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ??
          false)

      const matchesCategory =
        selectedCategory === 'all' || device.category === selectedCategory

      return matchesSearch && matchesCategory
    })
  }, [devices, searchQuery, selectedCategory])

  // Sort devices
  const sortedDevices = useMemo(() => {
    const sorted = [...filteredDevices]
    switch (sortBy) {
      case 'name':
        sorted.sort((a, b) => a.name.localeCompare(b.name))
        break
      case 'category':
        sorted.sort((a, b) => {
          const catA = CATEGORY_LABELS[a.category]
          const catB = CATEGORY_LABELS[b.category]
          return catA.localeCompare(catB)
        })
        break
      case 'connections':
        sorted.sort(
          (a, b) => getConnectionCount(b.id) - getConnectionCount(a.id),
        )
        break
    }
    return sorted
  }, [filteredDevices, sortBy])

  const handleSelectDevice = (deviceId: string) => {
    setSelection([deviceId])
  }

  const handleToggleSelect = (deviceId: string) => {
    const newSelected = new Set(localSelected)
    if (newSelected.has(deviceId)) {
      newSelected.delete(deviceId)
    } else {
      newSelected.add(deviceId)
    }
    setLocalSelected(newSelected)
  }

  const handleSelectAll = () => {
    if (localSelected.size === sortedDevices.length) {
      setLocalSelected(new Set())
    } else {
      setLocalSelected(new Set(sortedDevices.map(d => d.id)))
    }
  }

  const handleDeleteSelected = () => {
    if (localSelected.size === 0) return
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${localSelected.size} device(s)? This will also remove all their connections.`,
    )
    if (confirmDelete) {
      localSelected.forEach(deviceId => {
        deleteDevice(deviceId)
      })
      setLocalSelected(new Set())
    }
  }

  const handleDeleteSingle = (deviceId: string) => {
    const device = devices.find(d => d.id === deviceId)
    if (!device) return
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${device.name}"? This will also remove all its connections.`,
    )
    if (confirmDelete) {
      deleteDevice(deviceId)
    }
  }

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/30 z-30" onClick={onClose} />
      )}

      {/* Panel */}
      <div
        className={`fixed right-0 top-0 h-[100dvh] w-96 max-w-full bg-surface-base shadow-high z-40 transform transition-transform duration-300 ease-out overflow-hidden flex flex-col pt-safe pb-safe ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-brd-subtle">
          <h2 className="text-lg font-semibold text-content-primary">
            Manage Devices
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-surface-sunken rounded-lg transition"
            title="Close panel"
          >
            <svg
              className="w-5 h-5 text-content-secondary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Search Bar */}
          <div className="p-4 border-b border-brd-subtle space-y-3">
            <input
              type="text"
              placeholder="Search devices..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 bg-surface-sunken border border-brd-subtle rounded-lg text-sm text-content-primary placeholder-content-tertiary focus:outline-none focus:ring-2 focus:ring-accent/50"
            />

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={e =>
                setSelectedCategory(e.target.value as DeviceCategory | 'all')
              }
              className="w-full px-3 py-2 bg-surface-sunken border border-brd-subtle rounded-lg text-sm text-content-primary focus:outline-none focus:ring-2 focus:ring-accent/50"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {CATEGORY_LABELS[cat]}
                </option>
              ))}
            </select>

            {/* Sort Options */}
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value as SortOption)}
              className="w-full px-3 py-2 bg-surface-sunken border border-brd-subtle rounded-lg text-sm text-content-primary focus:outline-none focus:ring-2 focus:ring-accent/50"
            >
              <option value="name">Sort by Name</option>
              <option value="category">Sort by Category</option>
              <option value="connections">Sort by Connections</option>
            </select>
          </div>

          {/* Device List */}
          <div className="p-4 space-y-2">
            {sortedDevices.length === 0 ? (
              <div className="text-center py-8 text-content-tertiary">
                <p className="text-sm">No devices found</p>
              </div>
            ) : (
              <>
                {/* Select All */}
                <div className="flex items-center gap-2 px-2 py-2 text-sm font-medium text-content-secondary border-b border-brd-subtle">
                  <input
                    type="checkbox"
                    checked={
                      localSelected.size > 0 &&
                      localSelected.size === sortedDevices.length
                    }
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded border-brd-subtle text-accent focus:ring-2 focus:ring-accent/50 cursor-pointer"
                  />
                  <label className="cursor-pointer select-none flex-1">
                    Select All ({localSelected.size}/{sortedDevices.length})
                  </label>
                </div>

                {/* Device Rows */}
                {sortedDevices.map(device => (
                  <div
                    key={device.id}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent-muted transition-colors duration-150 group"
                  >
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      checked={localSelected.has(device.id)}
                      onChange={() => handleToggleSelect(device.id)}
                      className="w-4 h-4 rounded border-brd-subtle text-accent focus:ring-2 focus:ring-accent/50 cursor-pointer flex-shrink-0"
                    />

                    {/* Category Icon with Color Accent */}
                    <div className="flex-shrink-0">
                      <div
                        className="w-10 h-10 rounded-lg p-1 flex items-center justify-center"
                        style={{
                          backgroundColor: `${CATEGORY_COLORS[device.category]}20`,
                          borderLeft: `3px solid ${CATEGORY_COLORS[device.category]}`,
                        }}
                      >
                        <CategoryIcon category={device.category} size={20} />
                      </div>
                    </div>

                    {/* Device Info */}
                    <div
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => handleSelectDevice(device.id)}
                    >
                      <div className="font-medium text-sm text-content-primary truncate hover:text-accent">
                        {device.name}
                      </div>
                      <div className="text-xs text-content-tertiary truncate">
                        {device.manufacturer ? `${device.manufacturer} • ` : ''}
                        {CATEGORY_LABELS[device.category]}
                      </div>
                      <div className="text-xs text-content-tertiary mt-0.5">
                        <span>
                          {device.inputs.length}in • {device.outputs.length}out
                        </span>
                        {getConnectionCount(device.id) > 0 && (
                          <span className="ml-2 inline-block px-1.5 py-0.5 bg-accent-muted text-accent rounded text-xs font-medium">
                            {getConnectionCount(device.id)} connections
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Edit Button */}
                    <button
                      onClick={() => setEditingDevice(device)}
                      className="flex-shrink-0 p-2 text-content-tertiary hover:text-accent hover:bg-accent-muted rounded-lg transition-colors duration-150 opacity-0 group-hover:opacity-100"
                      title="Edit device (rename, add/remove ports)"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>

                    {/* Delete Button */}
                    <button
                      onClick={() => handleDeleteSingle(device.id)}
                      className="flex-shrink-0 p-2 text-content-tertiary hover:text-destructive hover:bg-destructive/5 rounded-lg transition-colors duration-150 opacity-0 group-hover:opacity-100"
                      title="Delete device"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        {/* Footer - Bulk Delete */}
        {localSelected.size > 0 && (
          <div className="border-t border-brd-subtle p-4 bg-surface-sunken">
            <button
              onClick={handleDeleteSelected}
              className="w-full btn btn-md bg-destructive text-white hover:bg-destructive/90 font-medium"
            >
              Delete Selected ({localSelected.size})
            </button>
          </div>
        )}
      </div>
      <EditDeviceDialog
        isOpen={editingDevice !== null}
        onClose={() => setEditingDevice(null)}
        device={editingDevice}
        onUpdate={d => {
          updateDevice(d)
          setEditingDevice(null)
        }}
      />
    </>
  )
}
